import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, User, CheckCircle, XCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import { getAssignmentAnalytics, getSubmissionDetail } from '../../services/studioApi';
import UnifiedHeader from '../../components/UnifiedHeader';
import './Studio.css';

export default function AssignmentResults() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (translations[language]?.studio) || {};

  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAssignmentAnalytics(assignmentId)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [assignmentId]);

  const loadDetail = async (submissionId) => {
    if (selected === submissionId) { setSelected(null); setDetail(null); return; }
    setSelected(submissionId);
    const d = await getSubmissionDetail(assignmentId, submissionId);
    setDetail(d);
  };

  const exportCSV = () => {
    if (!data?.submissions) return;
    const rows = data.submissions.map(s => [
      s.full_name, s.email,
      s.total_score, s.max_score,
      s.max_score > 0 ? Math.round((s.total_score / s.max_score) * 100) + '%' : '0%',
      s.attempt_number,
      s.time_taken_seconds ? Math.round(s.time_taken_seconds / 60) + 'min' : '',
      s.submitted_at ? new Date(s.submitted_at).toLocaleString() : ''
    ]);
    const header = ['Student', 'Email', 'Score', 'Max', 'Percentage', 'Attempt', 'Time', 'Submitted'];
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv,' + encodeURIComponent(csv);
    a.download = `results_${assignmentId}.csv`;
    a.click();
  };

  const pct = (s) => s.max_score > 0 ? Math.round((s.total_score / s.max_score) * 100) : 0;

  if (loading) return <div className="studio-page"><UnifiedHeader /><div className="loading-center"><div className="spinner" /></div></div>;

  return (
    <div className="studio-page">
      <UnifiedHeader subRow={
        <>
          <div className="studio-subheader-left">
            <button className="studio-back-btn" onClick={() => navigate('/studio/teacher/assignments')}>
              <ArrowLeft size={15} /> {t.back || 'Back'}
            </button>
            <h1 className="studio-page-title">
              {data?.assignment?.title || t.teacher?.results || 'Results'}
            </h1>
          </div>
          <button className="btn-secondary" onClick={exportCSV}>
            <Download size={15} /> {t.teacher?.exportCsv || 'Export CSV'}
          </button>
        </>
      } />

      <div className="studio-main">
        {/* Summary stats */}
        {data && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            {[
              { label: t.teacher?.submissions || t.teacher?.submissionCount || 'Submissions', value: data.submission_count },
              { label: t.teacher?.avgScore || 'Avg Score', value: `${Math.round(data.average_score * 10) / 10}` },
            ].map(stat => (
              <div key={stat.label} className="studio-card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: '#667eea' }}>{stat.value}</div>
                <div style={{ fontSize: '0.82rem', color: '#888' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Submissions table */}
        {data?.submissions?.length === 0 ? (
          <div className="studio-card">
            <p style={{ color: '#aaa', textAlign: 'center', padding: '2rem 0' }}>
              {t.teacher?.noSubmissionsYet || 'No submissions yet'}
            </p>
          </div>
        ) : (
          <div className="studio-card" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', minWidth: 480 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                  {[t.teacher?.student || 'Student', t.teacher?.score || 'Score', t.teacher?.attempt || 'Attempt', t.teacher?.timeCol || 'Time', t.teacher?.submittedAt || 'Submitted', ''].map(h => (
                    <th key={h} style={{ padding: '0.75rem', textAlign: 'left', color: '#555', fontWeight: 700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data?.submissions?.map(s => (
                  <React.Fragment key={s.id}>
                    <tr
                      style={{ borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}
                      onClick={() => loadDetail(s.id)}
                    >
                      <td style={{ padding: '0.75rem' }}>
                        <div style={{ fontWeight: 600 }}>{s.full_name}</div>
                        <div style={{ fontSize: '0.78rem', color: '#999' }}>{s.email}</div>
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <span style={{ fontWeight: 700, color: pct(s) >= 70 ? '#4caf50' : pct(s) >= 40 ? '#ff9800' : '#f44336' }}>
                          {pct(s)}%
                        </span>
                        <span style={{ color: '#aaa', fontSize: '0.8rem', marginLeft: 4 }}>({s.total_score}/{s.max_score})</span>
                      </td>
                      <td style={{ padding: '0.75rem', color: '#666' }}>{s.attempt_number}</td>
                      <td style={{ padding: '0.75rem', color: '#666' }}>
                        {s.time_taken_seconds ? `${Math.round(s.time_taken_seconds / 60)}min` : '—'}
                      </td>
                      <td style={{ padding: '0.75rem', color: '#888', fontSize: '0.8rem' }}>
                        {s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : '—'}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <button className="btn-secondary" style={{ padding: '0.3rem 0.6rem', fontSize: '0.78rem' }}>
                          {selected === s.id ? (t.teacher?.hide || 'Hide') : (t.teacher?.viewDetails || 'Details')}
                        </button>
                      </td>
                    </tr>
                    {selected === s.id && detail && (
                      <tr>
                        <td colSpan={6} style={{ padding: '0 0.75rem 1rem' }}>
                          <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '1rem' }}>
                            <h4 style={{ margin: '0 0 0.75rem', color: '#555' }}>
                              <User size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                              {detail.full_name} — {t.teacher?.aiFeedback || 'AI Feedback'}
                            </h4>
                            {detail.ai_feedback && Object.entries(detail.ai_feedback)
                              .sort(([aId], [bId]) => {
                                const aOrder = detail.questions_map?.[aId]?.order ?? 999;
                                const bOrder = detail.questions_map?.[bId]?.order ?? 999;
                                return aOrder - bOrder;
                              })
                              .map(([qId, fb]) => {
                                const qInfo = detail.questions_map?.[qId];
                                const qNum = qInfo?.order ?? '?';
                                return (
                                  <div key={qId} style={{ borderBottom: '1px solid #e8ecf0', paddingBottom: '0.65rem', marginBottom: '0.65rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', marginBottom: '0.3rem' }}>
                                      {fb.correct || fb.score >= 0.7
                                        ? <CheckCircle size={14} color="#4caf50" style={{ flexShrink: 0, marginTop: 2 }} />
                                        : <XCircle size={14} color="#f44336" style={{ flexShrink: 0, marginTop: 2 }} />}
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                                          <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#333' }}>
                                            Q{qNum}{qInfo?.text ? `: ${qInfo.text}` : ''}
                                          </span>
                                          <span style={{ fontWeight: 600, fontSize: '0.82rem', color: fb.score >= 0.7 ? '#2e7d32' : '#c62828', flexShrink: 0 }}>
                                            {Math.round((fb.score || 0) * 100)}%
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <p style={{ margin: '0 0 0.25rem 1.25rem', fontSize: '0.82rem', color: '#555' }}>{fb.feedback}</p>
                                    {fb.gaps?.length > 0 && (
                                      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginLeft: '1.25rem' }}>
                                        {fb.gaps.map((g, i) => <span key={i} className="badge badge-red">{g}</span>)}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
